// src/components/common/Boxview.jsx
import React, { useState, useEffect } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight, FaDownload, FaShare, FaHeart, FaComment } from 'react-icons/fa';
import './boxview.css';

const Boxview = ({ isOpen, onClose, images, initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isLoading, setIsLoading] = useState(true);
  const [isVideo, setIsVideo] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setCurrentIndex(initialIndex);
      checkIfVideo(images[initialIndex]?.url);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialIndex, images]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, images.length, onClose]);

  const checkIfVideo = (url) => {
    if (!url) return false;
    const videoExtensions = ['mp4', 'webm', 'ogg', 'mov'];
    const extension = url.split('.').pop()?.toLowerCase();
    const isVideoFile = videoExtensions.includes(extension || '');
    setIsVideo(isVideoFile || url.includes('video'));
  };

  const goToPrevious = (e) => {
    if (e) e.stopPropagation();
    setIsLoading(true);
    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    checkIfVideo(images[newIndex]?.url);
  };

  const goToNext = (e) => {
    if (e) e.stopPropagation();
    setIsLoading(true);
    const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    checkIfVideo(images[newIndex]?.url);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleVideoLoad = () => {
    setIsLoading(false);
  };

  const handleDownload = async () => {
    const currentImage = images[currentIndex];
    try {
      const response = await fetch(currentImage.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `murima-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur téléchargement:', error);
    }
  };

  const handleShare = async () => {
    const currentImage = images[currentIndex];
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'MURIMA',
          text: currentImage.caption || 'Photo partagée',
          url: currentImage.url
        });
      } catch (error) {
        console.log('Partage annulé');
      }
    } else {
      navigator.clipboard.writeText(currentImage.url);
      // Toast notification ici
    }
  };

  if (!isOpen || !images || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return (
    <div className="boxview-overlay" onClick={onClose}>
      <div className="boxview-container" onClick={(e) => e.stopPropagation()}>
        {/* Bouton fermeture */}
        <button className="boxview-close" onClick={onClose}>
          <FaTimes />
        </button>

        {/* Navigation */}
        {images.length > 1 && (
          <>
            <button 
              className="boxview-nav boxview-prev" 
              onClick={goToPrevious}
              aria-label="Image précédente"
            >
              <FaChevronLeft />
            </button>
            <button 
              className="boxview-nav boxview-next" 
              onClick={goToNext}
              aria-label="Image suivante"
            >
              <FaChevronRight />
            </button>
          </>
        )}

        {/* Contenu principal */}
        <div className="boxview-content-wrapper">
          {isLoading && (
            <div className="boxview-loader">
              <div className="spinner"></div>
            </div>
          )}
          
          {!isVideo ? (
            <img
              src={currentImage?.url}
              alt={currentImage?.caption || `Image ${currentIndex + 1}`}
              onLoad={handleImageLoad}
              className={`boxview-image ${isLoading ? 'loading' : 'loaded'}`}
            />
          ) : (
            <video
              src={currentImage?.url}
              controls
              autoPlay
              onLoadedData={handleVideoLoad}
              className={`boxview-video ${isLoading ? 'loading' : 'loaded'}`}
            />
          )}
        </div>

        {/* Informations et actions */}
        <div className="boxview-footer">
          <div className="boxview-counter">
            {currentIndex + 1} / {images.length}
          </div>
          
          {currentImage?.caption && (
            <div className="boxview-caption">{currentImage.caption}</div>
          )}
          
          <div className="boxview-actions">
            <button className="boxview-action" onClick={handleShare}>
              <FaShare /> Partager
            </button>
            <button className="boxview-action" onClick={handleDownload}>
              <FaDownload /> Télécharger
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Boxview;